import { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { PATHS } from '@belong/common';
import { Button } from '@belong/ui';
import {
  ListingApplicationListingApplicationModel,
  UserModel,
} from 'api/models';
import ApplicationInfoCard, {
  LeaseAdvisor,
} from 'containercomponents/ApplicationInfoCard/applicationInfoCard';
import { ApplicationStatusSteps } from 'containercomponents/ApplicationStatusSteps/ApplicationStatusSetps';
import { useModal } from 'hooks/useModal';
import RenterAccountBookmark from 'models/renterAccounts/RenterAccountBookmark';
import { resolveApplicationScreen } from 'pages/Accounts/Pages/ToursAndApps/ApplicationStatusUtils';
import { resolveLeaseSignatureStatus } from 'pages/Accounts/Pages/ToursAndApps/SignatureStatusUtils';
import { BASE_PATHS } from 'routes/paths';
import { fetchLeaseAdvisor } from 'store/redux/renter-accounts/actions';
import {
  selectLeaseAdvisorByKey,
  selectLeaseSignatureInformation,
} from 'store/redux/renter-accounts/selectors';
import { AppCardTitleWithDetails, AppCardTopContent } from './AppCardElements';
import { WithdrawModal } from './WithdrawModal';

interface LeaseSignatureInfo {
  leaseId: string;
  signatureInfo: {
    signMode: string;
    hasResidentCompletedSigningFlow: boolean;
    hasConciergeDownloadedFile: boolean;
    status: string;
  };
  isSelfServe?: boolean;
}

type Props = {
  bookmark: RenterAccountBookmark;
  user: UserModel;
  listingApplicationDetails?: ListingApplicationListingApplicationModel;
  dispatch: (action: any) => void;
  leaseAdvisor?: {
    [key: string]: unknown;
  };
  leasesSignatureInformation?: LeaseSignatureInfo[];
};

const AppsCard = ({
  bookmark,
  user,
  listingApplicationDetails,
  dispatch,
  leaseAdvisor,
  leasesSignatureInformation,
}: Props) => {
  const history = useHistory();
  const [isWithdrawModalOpen, , hideWithdrawModal, setShowWithdrawModal] =
    useModal(false);

  const { rentAmount, unit, applicationId, applicants } = bookmark;

  const { basicInfo } = unit;

  const homeId = bookmark.listingApplication.homeId;

  useEffect(() => {
    if (homeId) {
      dispatch(fetchLeaseAdvisor('Home', homeId, 'ResidentGrowth'));
    }
  }, [homeId, dispatch]);

  const currentListingApplicant =
    listingApplicationDetails?.application?.applicants?.find(
      (applicant) => applicant.residentUniqueId === user.userId,
    );

  let applicationScreen = resolveApplicationScreen(
    listingApplicationDetails,
    currentListingApplicant,
    applicationId,
    basicInfo.unitId,
  );

  const {
    cardTitle,
    cardDescription,
    showIncomeButton,
    showApplicantsStatus,
    showVerificationsStatus,
    showLeaseAdvisorContact,
  } = applicationScreen;

  const noBottomContent =
    !showIncomeButton &&
    !showApplicantsStatus &&
    !showVerificationsStatus &&
    !showLeaseAdvisorContact &&
    !cardTitle &&
    !cardDescription;
  const { mainApplicant } = applicants || {};
  const isMainApplicant = mainApplicant?.residentId === user.userId;

  const completeApplication = () => {
    history.push(
      (isMainApplicant
        ? PATHS.RESIDENT_APPLICATION_LEASE_STEP
        : PATHS.RESIDENT_APPLICATION_PRE_APPLICANT_FLOW
      ).concat(`?applicationId=${applicationId}&homeId=${basicInfo?.unitId}`),
    );
  };

  if (bookmark.listingApplication.status === 'Approved') {
    // Find the signature information for the current lease
    const currentLeaseSignatureInfo = leasesSignatureInformation?.find(
      (leaseSig) => leaseSig.leaseId === bookmark.leaseInfo?.basicInfo?.leaseId,
    );

    applicationScreen = resolveLeaseSignatureStatus(
      bookmark,
      currentListingApplicant,
      currentLeaseSignatureInfo?.signatureInfo,
      currentLeaseSignatureInfo?.isSelfServe,
    );
  }

  const handleSignLease = () => {
    if (applicationScreen.signingUrl) {
      history.push(applicationScreen.signingUrl);
    }
  };

  const hideButtons =
    applicationScreen.isSelfServe && applicationScreen.stepNumber === 5;

  return (
    <div
      className="flex flex-col gap-0"
      // A conditional rather than `&&`: the falsy branch of `&&` is `false`, which
      // is not a style object. `undefined` applies no style, as before.
      style={
        applicationScreen.hideSteps ? { filter: 'grayscale(1)' } : undefined
      }
    >
      <AppCardTopContent
        imageUrl={unit.basicInfo.bannerImageUrl}
        rounded={noBottomContent}
        setShowWithdrawModal={setShowWithdrawModal}
        listingApplicationDetails={listingApplicationDetails}
        topContent={
          <>
            <AppCardTitleWithDetails
              title={bookmark.address.getAddressAndUnitNumber(
                unit.basicInfo.unitNumber,
              )}
              basicInfo={basicInfo}
              rentAmount={rentAmount}
              homeUniqueId={basicInfo.unitId}
              isGrayscale={applicationScreen.hideSteps}
            />
            {!hideButtons && (
              <div className="mt-xl flex w-full flex-col-reverse items-end justify-end gap-sm md:mt-2xl md:h-2xl md:flex-row md:items-center">
                {!applicationScreen.hideSteps &&
                  !applicationScreen.signers &&
                  applicationScreen.screenNumber !== 1 &&
                  applicationScreen.screenNumber < 4 && (
                    <Button
                      variant="text"
                      size="auto"
                      onClick={() =>
                        history.push(
                          `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours`,
                        )
                      }
                    >
                      Tour Home
                    </Button>
                  )}

                {!applicationScreen.hideSteps &&
                  !applicationScreen.signers &&
                  applicationScreen.screenNumber === 1 && (
                    <Button
                      variant="text"
                      size="fluid"
                      className="w-full md:w-auto"
                      onClick={() =>
                        history.push(
                          `${BASE_PATHS.HOME}/${basicInfo.unitId}?view=tours`,
                        )
                      }
                    >
                      Tour Home
                    </Button>
                  )}

                {applicationScreen.screenNumber === 1 && (
                  <Button
                    variant="solid"
                    size={{ md: 'auto', _: 'fluid' }}
                    className="whitespace-nowrap !px-sm "
                    onClick={completeApplication}
                  >
                    Complete Application
                  </Button>
                )}

                {applicationScreen.signers && (
                  <Button
                    variant="solid"
                    size={{ md: 'auto', _: 'fluid' }}
                    className="whitespace-nowrap !px-sm"
                    disabled={!applicationScreen.signingUrl}
                    onClick={handleSignLease}
                  >
                    Sign Lease
                  </Button>
                )}
              </div>
            )}
          </>
        }
        bottomContent={
          <>
            {!applicationScreen.hideSteps && (
              <ApplicationStatusSteps
                activeStep={applicationScreen.stepNumber || 0}
                completionPercentage={
                  currentListingApplicant?.completionPercentage
                }
                isSelfServe={applicationScreen.isSelfServe}
              />
            )}
          </>
        }
      />
      {!noBottomContent && (
        <ApplicationInfoCard
          applicationScreen={applicationScreen}
          listingApplicationDetails={listingApplicationDetails}
          currentApplicant={currentListingApplicant}
          isGrayscale={applicationScreen.hideSteps}
          leaseAdvisor={leaseAdvisor as LeaseAdvisor}
          currentListingApplicant={currentListingApplicant}
        />
      )}
      <WithdrawModal
        onHide={
          typeof hideWithdrawModal === 'function'
            ? hideWithdrawModal
            : undefined
        }
        show={!!isWithdrawModalOpen}
        applicationId={bookmark.applicationId}
        unitId={basicInfo.unitId}
      />
    </div>
  );
};

function mapStateToProps(state: any, ownProps: any) {
  const { homeId } = ownProps.bookmark.listingApplication;
  const leaseAdvisorKey = `Home_${homeId}_ResidentGrowth`;

  return {
    leaseAdvisor: selectLeaseAdvisorByKey(state, leaseAdvisorKey),
    leasesSignatureInformation: selectLeaseSignatureInformation(state),
  };
}

export default connect(mapStateToProps)(AppsCard);
